import { modal } from '@repo/ui';
import { AutomationExecutionNodeStatusEnum, AutomationStatus } from '@repo/utils';
import { CaseUtil } from '@vectord/utils';
import { type NodeProps, useUpdateNodeInternals } from '@xyflow/react';
import useConditionNodeConfig from 'automations/hooks/conditionNodes/useConditionNodeConfig';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { useConfig } from 'automations/hooks/useConfig';
import { automationStatusSelector } from 'automations/selectors/automationConfig';
import { allowAnimationSelector, childrenCountSelector } from 'automations/selectors/reactFlow';
import { addNodeWithPosition } from 'automations/store/reactFlowSlice';
import { type ConditionNodeType } from 'automations/types/nodeTypes';
import { deleteNode } from 'automations/utils/deleteNode';
import { generateNewNodePayload } from 'automations/utils/node';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import NodeUi from '../NodeUi';
import styles from './ConditionNode.module.scss';

export const DEFAULT_HANDLE_ID = 'default';

export default function ConditionNode(node: NodeProps<ConditionNodeType>) {
  const { id, data } = node;
  const dispatch = useAutomationDispatch();
  const childrenCount = useSelector(childrenCountSelector(id));

  const { conditions } = useConfig();
  const updateNodeInternals = useUpdateNodeInternals();
  const { setMenuOpen } = useCommonNodeHandles(node);

  const automationStatus = useSelector(automationStatusSelector);
  const targetExecStatus = node?.data?.executionStatus;

  const allowAnimation = useSelector(allowAnimationSelector(id));

  const backgroundStatus = useMemo(() => {
    if (!allowAnimation) return undefined;
    if (targetExecStatus === AutomationExecutionNodeStatusEnum.SUCCESS) return 'success';
    if (targetExecStatus === AutomationExecutionNodeStatusEnum.FAILED) return 'failed';
    return undefined;
  }, [allowAnimation, targetExecStatus]);

  const {
    subTitle,
    icon: Icon,
    dangerIcon,
    hasErrors,
    errorMessages,
    handleEdit,
    handleCopy,
    AdditionalUI,
    targetHandle,
    canOpenMenu = true,
    canDelete = true,
  } = useConditionNodeConfig(node);

  useEffect(() => {
    // notify react flow to update the node internals
    updateNodeInternals(id);
  }, [updateNodeInternals, id]);

  const conditionContent = useMemo(() => conditions?.find(
    (c) => c.name === data.nodeSubType,
  ), [data.nodeSubType, conditions]);

  const handleAddNode = useCallback(() => {
    const newNode = generateNewNodePayload({
      data: {
        executionOrder: childrenCount + 1,
        sourceHandle: DEFAULT_HANDLE_ID,
      },
    });
    dispatch(addNodeWithPosition({
      newNode,
      sourceNodeId: id,
    }));
  }, [childrenCount, dispatch, id]);

  const handleDeleteNode = useCallback(() => {
    modal.confirm({
      title: 'Are you sure you want to delete this node?',
      content: 'NOTE: This action cannot be undone, All child nodes of this node will also be deleted.',
      centered: true,
      width: 600,
      onOk: () => dispatch(deleteNode(id)),
      okText: 'Delete',
      okButtonProps: {
        danger: true,
      },
    });
  }, [dispatch, id]);

  const canDeleteNode = canDelete && automationStatus === AutomationStatus.draft;

  return (
    <NodeUi
      nodeId={id}
      nodeTitle={CaseUtil.titleCase(data.nodeSubType)}
      nodeSubtitle={subTitle ?? conditionContent?.desc ?? ''}
      icon={{
        iconContent: <Icon className={clsx(styles.iconContent, dangerIcon && styles.dangerIcon)} />,
        iconBgClassName: clsx(styles.iconBg, dangerIcon && styles.dangerIconBg),
      }}
      hasErrors={hasErrors}
      errorMessages={errorMessages}
      sourceHandle={{}}
      targetHandle={targetHandle && {
        onClick: targetHandle?.onClick ?? handleAddNode,
        id: targetHandle?.id ?? DEFAULT_HANDLE_ID,
      }}
      additionalUiClassName={styles.additionalUi}
      AdditionalUI={AdditionalUI}
      contextMenu={{
        onEdit: handleEdit ?? (canOpenMenu ? () => setMenuOpen(true) : undefined),
        onDelete: canDeleteNode ? handleDeleteNode : undefined,
        onCopy: handleCopy,
      }}
      debugContent={data}
      successExecutionCounts={data?.successExecutionCounts}
      backgroundStatus={backgroundStatus}
    />
  );
}
